import { useState, forwardRef, useImperativeHandle } from 'react'
import {
  TextInput,
  Toggle,
  DatePicker,
  DatePickerInput,
  Stack,
  Row,
  Column,
} from '@carbon/react'
import {
  ModalWithButton,
  ModalFormContentProps,
  ModalFormHandle,
} from '@sphere/shared/ui/common'
import styles from './GlobalAssignmentEditModal.module.scss'
import { Settings } from '@carbon/icons-react'

export interface GlobalAssignmentOptions {
  availableFrom?: Date
  dueAt?: Date
  maxAttempts?: number
  showSolutions: boolean
  randomizeOrder: boolean
  isVisible: boolean
}

interface GlobalAssignmentEditModalProps {
  onSave: (options: GlobalAssignmentOptions) => Promise<void>
  isSaving: boolean
  disabled?: boolean
}

/**
 * Composant de contenu du formulaire.
 * Utilise forwardRef + useImperativeHandle pour exposer getData() au parent.
 */
const GlobalAssignmentFormContent = forwardRef<
  ModalFormHandle<GlobalAssignmentOptions>,
  ModalFormContentProps
>(({ closeModal }, ref) => {
  const [options, setOptions] = useState<GlobalAssignmentOptions>({
    availableFrom: new Date(),
    dueAt: new Date(),
    maxAttempts: 1,
    showSolutions: false,
    randomizeOrder: false,
    isVisible: true,
  })

  // Expose getData() au parent via la ref
  useImperativeHandle(ref, () => ({
    getData: () => options,
  }))

  return (
    <Stack gap={6}>
      <p style={{ marginBottom: '1rem' }}>
        Ces modifications s'appliqueront à toutes les assignations existantes.
      </p>

      {/* Première ligne : 2 dates + tentatives max */}
      <div className={styles.grid}>
        <DatePicker
          locale="fr"
          dateFormat="d/m/Y"
          datePickerType="single"
          value={options.availableFrom}
          onChange={(dates: Date[]) => {
            if (dates && dates[0]) {
              setOptions(prev => ({ ...prev, availableFrom: dates[0] }))
            }
          }}
        >
          <DatePickerInput
            id="availableFrom"
            labelText="Disponible du"
            placeholder="mm/jj/aaaa"
          />
        </DatePicker>
        <DatePicker
          locale="fr"
          dateFormat="d/m/Y"
          datePickerType="single"
          value={options.dueAt}
          onChange={(dates: Date[]) => {
            if (dates && dates[0]) {
              setOptions(prev => ({ ...prev, dueAt: dates[0] }))
            }
          }}
        >
          <DatePickerInput
            id="dueAt"
            labelText="À rendre pour"
            placeholder="mm/jj/aaaa"
          />
        </DatePicker>
        <TextInput
          id="maxAttempts"
          labelText="Tentatives max"
          type="number"
          min="1"
          value={options.maxAttempts?.toString() || ''}
          onChange={e => {
            setOptions(prev => ({
              ...prev,
              maxAttempts: e.target.value
                ? parseInt(e.target.value)
                : undefined,
            }))
          }}
        />
      </div>

      {/* Options avec Toggles */}
      <Stack gap={5}>
        <Row>
          <Column>
            <Toggle
              id="showSolutions"
              labelText="Afficher les solutions"
              toggled={options.showSolutions}
              onToggle={checked =>
                setOptions(prev => ({ ...prev, showSolutions: checked }))
              }
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Toggle
              id="randomizeOrder"
              labelText="Ordre aléatoire (Classes)"
              toggled={options.randomizeOrder}
              onToggle={checked =>
                setOptions(prev => ({ ...prev, randomizeOrder: checked }))
              }
            />
          </Column>
        </Row>
        <Row>
          <Column>
            <Toggle
              id="isVisible"
              labelText="Visible pour les élèves"
              toggled={options.isVisible}
              onToggle={checked =>
                setOptions(prev => ({ ...prev, isVisible: checked }))
              }
            />
          </Column>
        </Row>
      </Stack>
    </Stack>
  )
})

GlobalAssignmentFormContent.displayName = 'GlobalAssignmentFormContent'

export function GlobalAssignmentEditModal({
  onSave,
  isSaving,
  disabled = false,
}: GlobalAssignmentEditModalProps) {
  const handleSave = async (
    options: GlobalAssignmentOptions,
    closeModal: () => void
  ) => {
    await onSave(options)
    closeModal()
  }

  return (
    <ModalWithButton<GlobalAssignmentOptions>
      buttonLabel="Options globales"
      buttonIcon={Settings}
      iconDescription="Modifier les options globales"
      buttonSize="sm"
      buttonKind="ghost"
      disabled={disabled}
      modalHeading="Modifier les options pour tous"
      modalSize="md"
      primaryButtonText="Appliquer à tous"
      secondaryButtonText="Annuler"
      primaryButtonDisabled={isSaving}
      onPrimarySubmit={handleSave}
      FormContent={GlobalAssignmentFormContent}
    />
  )
}
